import { Spin } from "antd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  BuilderLayoutContainer,
  ButtonsContainer,
  CancelButton,
  Header,
  purpleButton,
  SectionButton,
  SectionsContainer,
  SectionsHeader,
  spinCss,
  Underline,
  whiteButton,
} from "./BuilderLayout.styles";

type LocationType = "Workflows" | "Protocols" | "Articles" | "Surveys" | "Rag";

export interface ISectionButton {
  isSelected: boolean;
  label: string;
  onButtonClick: (section: string) => void;
  name: string;
}

// export interface ISectionButtonSurveys extends ISectionButton {
//     onB
// }

interface IBuilderLayoutPropsBase {
  hasDashboardLayout?: boolean;
  children: ReactNode;
  isLayoutLoading?: boolean;
  isWhiteButtonLoading?: boolean;
  isPurpleButtonLoading?: boolean;
  isSaveDisabled?: boolean;
  cancelLabel?: string;
  /** Replaces the main (white) button's label, e.g. the survey builder's "Publish". */
  saveLabel?: string;
  /** More header actions, shown like Cancel (e.g. the survey builder's "Discard draft"). */
  extraActions?: { label: string; onClick: () => void }[];
  lastEdited?: string;
  location: LocationType;
  handleOnCancel: () => void;
  isPublished?: boolean;
  handleOnSave: (isPublished: boolean) => void;
  sectionButtons?: ISectionButton[];
}

interface IWorkflowBuilder extends IBuilderLayoutPropsBase {
  location: "Workflows";
  handleOnSave: (publish: boolean, isSave?: boolean) => void;
}

interface IProtocolBuilder extends IBuilderLayoutPropsBase {
  location: "Protocols";
  isPublished: boolean;
}

interface IArticleBuilder extends IBuilderLayoutPropsBase {
  location: "Articles";
  isPublished: boolean;
}

interface ISurveyBuilder extends IBuilderLayoutPropsBase {
  location: "Surveys";
  handleOnSave: () => void;
}

interface IRagBuilder extends IBuilderLayoutPropsBase {
  location: "Rag";
  handleOnSave: () => void;
}

type IBuilderLayoutProps =
  | IWorkflowBuilder
  | IProtocolBuilder
  | ISurveyBuilder
  | IArticleBuilder
  | IRagBuilder;

export function BuilderLayout({
  lastEdited,
  location,
  isLayoutLoading,
  isPurpleButtonLoading,
  hasDashboardLayout,
  sectionButtons,
  children,
  isWhiteButtonLoading,
  isSaveDisabled,
  cancelLabel,
  saveLabel,
  extraActions,
  isPublished,
  handleOnCancel,
  handleOnSave,
}: IBuilderLayoutProps) {
  const { t } = useTranslation();

  const getPurpleButtonLabel = () => {
    if ((location === "Protocols" || location === "Articles") && isPublished)
      return t("builder_unpublish");
    return t("builder_save");
  };

  const getWhiteButtonLabel = () => {
    if (saveLabel) return saveLabel;
    if (location === "Surveys" || location === "Rag") {
      return t("builder_save");
    }
    if (isPublished) {
      return t("builder_publishChanges");
    }
    return t("builder_publish");
  };

  const handleOnClickPurpleButton = () => {
    if (location === "Protocols" || location === "Articles") {
      handleOnSave(false);
    } else {
      handleOnSave(isPublished ?? false, true);
    }
  };

  return (
    <BuilderLayoutContainer hasDashboardLayout={hasDashboardLayout}>
      {isLayoutLoading ? (
        <Spin className={spinCss()} fullscreen size="large" />
      ) : (
        <>
          <Header>
            {lastEdited && (
              <p>
                {t("builder_lastEdited", {
                  date: getLongFormattedDate(lastEdited),
                })}
              </p>
            )}

            <ButtonsContainer>
              {extraActions?.map((action) => (
                <CancelButton
                  key={action.label}
                  onClick={action.onClick}
                  onKeyDown={(e) => e.key === "Enter" && action.onClick()}
                  tabIndex={0}
                  role="button"
                >
                  {action.label}
                </CancelButton>
              ))}
              <CancelButton onClick={handleOnCancel} tabIndex={0}>
                {cancelLabel ?? t("builder_cancel")}
              </CancelButton>
              {location !== "Surveys" && location !== "Rag" ? (
                <Button
                  disabled={isPurpleButtonLoading}
                  label={getPurpleButtonLabel()}
                  type="submit"
                  onClick={handleOnClickPurpleButton}
                  className={purpleButton()}
                  isLoading={isPurpleButtonLoading}
                />
              ) : null}

              <Button
                disabled={isWhiteButtonLoading || isSaveDisabled}
                label={getWhiteButtonLabel()}
                type="button"
                onClick={() => handleOnSave(true)}
                className={whiteButton()}
                isLoading={isWhiteButtonLoading}
              />
            </ButtonsContainer>
          </Header>
          {sectionButtons && (
            <SectionsHeader>
              <SectionsContainer>
                {sectionButtons.map((button, index) => (
                  <SectionButton
                    key={index}
                    onClick={() => button.onButtonClick(button.name)}
                    onKeyDown={(e) =>
                      e.key === "Enter" && button.onButtonClick(button.name)
                    }
                    tabIndex={0}
                  >
                    <p>{button.label}</p>
                    <Underline isActive={button.isSelected} />{" "}
                  </SectionButton>
                ))}
              </SectionsContainer>
            </SectionsHeader>
          )}

          {children}
        </>
      )}
    </BuilderLayoutContainer>
  );
}
